import {
  type ApiTaskSettingsModifiers,
  DEFAULT_DEFERRED_TASK_MODIFIER,
  DEFAULT_TENANT_RESPONSIBILITY_TASK_MODIFIER,
  DEFERRED_TASK_MODIFIER_KEY,
  type TaskModifierType,
  TENANT_RESPONSIBILITY_TASK_MODIFIER_KEY,
} from '@kaiizen/kai-pro-api';

/**
 * The tag a modifier writes onto a line item. Not a closed set any more: an org mints a tag for
 * each Display modifier it creates (KAIDEV-3182). The two financial tags keep their fixed names,
 * because `changeOrderLib` and the reports still branch on those literals.
 */
export type TaskModifierLineItemTag = string;

/** A modifier resolved for the UI: its key, the tag it writes, and the name the org gave it. */
export type TaskModifier = {
  key: string;
  lineItemTag: TaskModifierLineItemTag;
  displayName: string;
};

/** Financial types lead the menu, in this order; Display modifiers follow in record order. */
const TASK_MODIFIER_TYPE_ORDER: TaskModifierType[] = ['deferred', 'resident', 'display'];

const DEFAULT_DISPLAY_NAME_BY_MODIFIER_KEY: Record<string, string> = {
  [DEFERRED_TASK_MODIFIER_KEY]: DEFAULT_DEFERRED_TASK_MODIFIER.displayName ?? 'Deferred',
  [TENANT_RESPONSIBILITY_TASK_MODIFIER_KEY]: DEFAULT_TENANT_RESPONSIBILITY_TASK_MODIFIER.displayName ?? 'Resident Responsible',
};

/** Reserved keys predate `type`; a record that omits it is one of those two. */
const TYPE_BY_RESERVED_KEY: Record<string, TaskModifierType> = {
  [DEFERRED_TASK_MODIFIER_KEY]: 'deferred',
  [TENANT_RESPONSIBILITY_TASK_MODIFIER_KEY]: 'resident',
};

const getModifierSortIndex = (key: string, modifier: ApiTaskSettingsModifiers): number => {
  const type = modifier.type ?? TYPE_BY_RESERVED_KEY[key];
  const index = type ? TASK_MODIFIER_TYPE_ORDER.indexOf(type) : -1;
  return index === -1 ? TASK_MODIFIER_TYPE_ORDER.length : index;
};

const isModifierEnabledForUser = (
  modifier: ApiTaskSettingsModifiers,
  isFieldUser: boolean,
): boolean => {
  if (modifier.isEnabled === false) return false;
  if (isFieldUser && modifier.isEnabledFieldUser === false) return false;
  return true;
};

const toSortedTaskModifiers = (
  entries: [string, ApiTaskSettingsModifiers][],
): TaskModifier[] =>
  entries
    .map(([key, modifier]): TaskModifier & { sortIndex: number } => ({
      key,
      // The tag is the modifier's identity in scan data; the key is the fallback the reserved
      // rows have always used.
      lineItemTag: modifier.modifierTag?.trim() || key,
      displayName: modifier.displayName?.trim() || DEFAULT_DISPLAY_NAME_BY_MODIFIER_KEY[key] || key,
      sortIndex: getModifierSortIndex(key, modifier),
    }))
    .sort((first, second) => first.sortIndex - second.sortIndex)
    .map(({ sortIndex: _sortIndex, ...modifier }) => modifier);

/**
 * Resolves org-configured task modifiers into kebab menu entries.
 *
 * Enablement = key presence in `taskSettings.modifiers`, minus explicit
 * `isEnabled` / `isEnabledFieldUser` false flags (absent means enabled).
 *
 * This is the list of modifiers a user may *apply*. Anything that displays or filters an already
 * applied modifier wants `getKnownTaskModifiers` instead.
 */
export const getEnabledTaskModifiers = (
  modifiers: Record<string, ApiTaskSettingsModifiers> | undefined,
  { isFieldUser = false }: { isFieldUser?: boolean } = {},
): TaskModifier[] =>
  // Every configured key, not a fixed pair: an org's Display modifiers are keys only it knows.
  modifiers
    ? toSortedTaskModifiers(
      Object.entries(modifiers).filter(([, modifier]) => isModifierEnabledForUser(modifier, isFieldUser)),
    )
    : [];

/**
 * The two modifiers that predate the settings record. Their tags sit on live scan data whether or
 * not an org ever configured a row for them, and turning the old Outputs toggle off deleted the
 * key outright - so an absent row cannot be read as "this org has no deferred work".
 */
const RESERVED_TASK_MODIFIERS: Record<string, ApiTaskSettingsModifiers> = {
  [DEFERRED_TASK_MODIFIER_KEY]: DEFAULT_DEFERRED_TASK_MODIFIER,
  [TENANT_RESPONSIBILITY_TASK_MODIFIER_KEY]: DEFAULT_TENANT_RESPONSIBILITY_TASK_MODIFIER,
};

/**
 * Every modifier whose tag could be on a line item: all configured keys regardless of
 * `isEnabled`, plus the two reserved ones when the record has no row for them.
 *
 * Display and filtering read this, never the enabled list. Disabling a modifier removes it from
 * the picker and nothing else - line items already tagged keep showing the tag, keep their
 * behaviour, and stay filterable ("Nothing retroactive" in the spec). Field-user visibility is
 * not consulted either: it governs who may apply a modifier, not who may read one.
 */
export const getKnownTaskModifiers = (
  modifiers: Record<string, ApiTaskSettingsModifiers> | undefined,
): TaskModifier[] => toSortedTaskModifiers(Object.entries({ ...RESERVED_TASK_MODIFIERS, ...modifiers }));

export const hasEnabledTaskModifiers = (
  modifiers: Record<string, ApiTaskSettingsModifiers> | undefined,
  options?: { isFieldUser?: boolean },
): boolean => getEnabledTaskModifiers(modifiers, options).length > 0;
